import { Dialog, GridHookForm } from 'vnetwork-ui'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useModal } from '@/contexts/modal'
import { useState } from 'react'
import useUploadFile from '@/hooks/useUploadFile'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { renderFileItem } from '@/utils/function'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'

interface CancelNegotiationProps extends ModalProps {
    negotiationId: string
    onSubmit: (data: any) => Promise<void>
}

interface CancelFormData {
    reason: string
    notes?: string
    attachments: File[]
}

const CancelNegotiation: React.FC<CancelNegotiationProps> = ({
    onClose,
    open,
    onSubmit,
}) => {
    const { t } = useTranslation()
    const { closeModal } = useModal()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const { uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.CONTRACT_NEGOTIATION,
    })

    const formControl = useForm<CancelFormData>({
        defaultValues: {
            reason: '',
            notes: '',
            attachments: [],
        },
    })

    const handleSubmit = async (values: CancelFormData) => {
        setIsSubmitting(true)
        try {
            let attachments: Attachment[] = []
            if (values.attachments && values.attachments.length > 0) {
                const attachmentUploadeds = await uploadAsync(
                    values.attachments
                )
                attachments = attachmentUploadeds
                    ?.filter((attachment) => attachment.status === 'fulfilled')
                    .map((attachment) => attachment.value) as Attachment[]
            }

            await onSubmit({
                reason: values.reason,
                notes: values.notes,
                attachments,
            })
            closeModal()
        } catch (error) {
            console.error('Error cancelling negotiation:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onClose={onClose} title={t('cancel-negotiation')}>
            <div className="max-w-2xl">
                <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        {t('cancel-negotiation')}
                    </h2>
                    <p className="text-gray-600">
                        {t('cancel-negotiation-description')}
                    </p>
                </div>

                <GridHookForm
                    formControl={formControl}
                    onSubmit={handleSubmit}
                    actions={[
                        {
                            title: t('cancel'),
                            type: 'button',
                            variant: 'outlined',
                            handler: closeModal,
                        },
                        {
                            title: t('cancel-negotiation'),
                            type: 'submit',
                            variant: 'contained',
                            loading: isSubmitting,
                            disabled: isSubmitting,
                        },
                    ]}
                    data={[
                        {
                            formItemProps: {
                                name: 'reason',
                                type: 'textarea',
                                label: t('cancellation-reason'),
                                placeholder: t('enter-cancellation-reason'),
                            },
                            className: 'col-span-full',
                        },
                        {
                            formItemProps: {
                                name: 'notes',
                                type: 'textarea',
                                label: t('additional-notes'),
                                placeholder: t('enter-additional-notes'),
                            },
                            className: 'col-span-full',
                        },
                        {
                            formItemProps: {
                                name: 'attachments',
                                type: 'files',
                                multiple: true,
                                label: t('attachments'),
                                placeholder: t('select-attachments'),
                                renderFileItem: (file: File) => {
                                    return renderFileItem(file, () => {})
                                },
                            },
                            className: 'col-span-full',
                        },
                    ]}
                />
            </div>
        </Dialog>
    )
}

export default CancelNegotiation
